import { useCallback, useEffect, useRef, useState } from "react";

// Generic "load more on scroll" state machine. `fetchPage(cursor)` is called
// with `undefined` for the first page and whatever `fetchPage` last returned
// as `nextCursor` for every page after — each caller defines what a cursor
// means for it (an ISO timestamp, an offset, a switch from one endpoint to
// another partway through). Refetches from page 1 whenever `deps` change.
export function useInfiniteScroll(fetchPage, deps = []) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const cursorRef = useRef(undefined);
  const fetchPageRef = useRef(fetchPage);
  fetchPageRef.current = fetchPage;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setItems([]);
    setHasMore(true);
    cursorRef.current = undefined;

    fetchPageRef.current(undefined).then(({ items: page, nextCursor, hasMore: more }) => {
      if (cancelled) return;
      setItems(page);
      cursorRef.current = nextCursor;
      setHasMore(!!more);
      setLoading(false);
    }).catch(() => {
      if (!cancelled) setLoading(false);
    });

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  const loadMore = useCallback(() => {
    if (loading || loadingMore || !hasMore) return;
    setLoadingMore(true);
    fetchPageRef.current(cursorRef.current).then(({ items: page, nextCursor, hasMore: more }) => {
      setItems((prev) => [...prev, ...page]);
      cursorRef.current = nextCursor;
      setHasMore(!!more);
    }).finally(() => setLoadingMore(false));
  }, [loading, loadingMore, hasMore]);

  // `setItems` is exposed for local optimistic updates (e.g. patching one
  // row's status after an action) — cheaper and less jarring than refetching
  // page 1 and losing scroll position through several already-loaded pages.
  return { items, setItems, loading, loadingMore, hasMore, loadMore };
}
